<script setup lang="ts">
import { ref, watch } from 'vue'
import EligibilityDetails from './EligibilityDetails.vue'
import { XMarkIcon, ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
import HelpButton from '@/components/common/help/HelpButton.vue'
import { useContextualHelp } from '@/composables/useContextualHelp'

interface Props {
  isOpen: boolean
  eligibilityData: any
  loadingMessage?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  export: []
}>()

const modalRef = ref<HTMLDialogElement | null>(null)

useContextualHelp('eligibility-details', () => props.isOpen)

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue && modalRef.value) {
      modalRef.value.showModal()
    } else if (!newValue && modalRef.value) {
      modalRef.value.close()
    }
  },
)

function handleClose() {
  emit('close')
}

function handleExport() {
  emit('export')
}
</script>

<template>
  <dialog ref="modalRef" class="modal" @close="handleClose">
    <div class="modal-box max-w-7xl w-full max-h-[90vh] overflow-hidden">
      <!-- En-tête de la modal -->
      <div class="flex items-center justify-between mb-4 top-0 bg-base-100 z-10 pb-4 border-b">
        <div class="flex items-center gap-1">
          <h3 class="font-bold text-lg">Détails de l'éligibilité All-In-One</h3>
          <HelpButton />
        </div>
        <div class="flex gap-2">
          <router-link
            v-if="eligibilityData?.uuid"
            :to="`/eligibility/${eligibilityData.uuid}`"
            class="btn btn-sm btn-ghost"
            @click="handleClose"
          >
            <ArrowTopRightOnSquareIcon class="w-4 h-4 mr-1" />
            Ouvrir la page
          </router-link>
          <button v-if="eligibilityData" class="btn btn-sm btn-outline" @click="handleExport()">
            <ArrowDownTrayIcon class="w-4 h-4 mr-1" />
            CSV
          </button>
          <button class="btn btn-sm btn-circle btn-ghost" @click="handleClose">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Contenu de la modal -->
      <div class="overflow-y-auto max-h-[calc(85vh-8rem)] gap-4">
        <EligibilityDetails
          v-if="eligibilityData"
          :result="eligibilityData.result"
          :fulladdress="eligibilityData.fulladdress"
          :company-name="eligibilityData.company.name"
          :company-siret="eligibilityData.company.siret"
          :show-filters="true"
        />
        <div v-else class="flex flex-col justify-center items-center p-8 gap-4">
          <span class="loading loading-spinner loading-lg"></span>
          <p v-if="loadingMessage" class="text-center text-base-content/80 max-w-md">{{ loadingMessage }}</p>
        </div>
      </div>

      <!-- Pied de la modal -->
      <div class="modal-action sticky bottom-0 bg-base-100 pt-4 border-t mt-4">
        <button class="btn" @click="handleClose">Fermer</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="handleClose">close</button>
    </form>
  </dialog>
</template>
